import { ActionButton } from "../components/ActionButton";
import { AppInstallationGuideStepsSection } from "../components/AppInstallationGuideStepsSection";

function DownloadIcon() {
  return (
    <svg
      aria-hidden="true"
      className="oac-installation-step-icon"
      fill="none"
      viewBox="0 0 32 32"
    >
      <path
        d="M16 3V20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
      <path
        d="M10 14L16 20L22 14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        d="M5 19V27H27V19"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function PermissionIcon() {
  return (
    <svg
      aria-hidden="true"
      className="oac-installation-step-icon"
      fill="none"
      viewBox="0 0 32 32"
    >
      <rect
        fill="currentColor"
        height="14"
        rx="7"
        width="30"
        x="1"
        y="9"
      />
      <circle
        cx="23"
        cy="16"
        fill="#ffffff"
        r="5.5"
      />
    </svg>
  );
}

function InstallIcon() {
  return (
    <svg
      aria-hidden="true"
      className="oac-installation-step-icon"
      fill="none"
      viewBox="0 0 32 32"
    >
      <path
        d="M16 3.5L18.2 6.1L21.6 5.4L22.4 8.8L25.8 9.7L25.1 13.1L27.8 15.3L25.7 18.1L27.1 21.3L24 22.8L23.9 26.3L20.4 26.5L18.8 29.6L15.7 28.1L12.8 30L10.7 27.2L7.3 27.8L6.5 24.4L3.2 23.4L4 20L1.4 17.7L3.6 15L2.3 11.8L5.5 10.4L5.7 6.9L9.2 6.8L10.9 3.8L14 5.4L16 3.5Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.55"
      />
      <circle
        cx="15"
        cy="16.5"
        r="6.1"
        stroke="currentColor"
        strokeWidth="1.55"
      />
      <path
        d="M12.2 16.6L14.3 18.7L18.4 14.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

const installationSteps = [
  {
    id: "download",
    icon: <DownloadIcon />,
    label: "Download APK",
  },
  {
    id: "permission",
    icon: <PermissionIcon />,
    label: "Allow Permission",
  },
  {
    id: "install",
    icon: <InstallIcon />,
    label: "Install the App.",
  },
] as const;

function scrollToInstallationGuideSteps() {
  if (
    typeof window === "undefined" ||
    !window.matchMedia("(max-width: 67.9375rem)").matches
  ) {
    return;
  }

  document
    .getElementById("oac-installation-guide-steps")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function AppInstallationGuidePage() {
  return (
    <>
    <article className="oac-installation-page" id="oac-installation-guide-top">
      <section className="oac-installation-page__copy">
        <h1 className="oac-installation-page__heading">
          <span className="oac-installation-page__heading-desktop-only">
            <span className="oac-installation-page__heading-line">
              Install Android Apps
            </span>

            <span className="oac-installation-page__heading-line">
              in{" "}
              <span className="oac-installation-page__accent">
                3 Easy Steps.
              </span>
            </span>
          </span>

          <span className="oac-installation-page__heading-mobile-only">
            <span className="oac-installation-page__heading-mobile-line">
              Install
            </span>

            <span className="oac-installation-page__heading-mobile-line">
              Android Apps In
            </span>

            <span className="oac-installation-page__heading-mobile-line">
              <span className="oac-installation-page__accent">
                3 Easy Steps.
              </span>
            </span>
          </span>
        </h1>

        <div className="oac-installation-page__supporting-copy">
          <p className="oac-installation-page__trusted">
            Trusted by Developers. In Your Control.
          </p>

          <p>
            Don&apos;t worry. This is a standard Android security step.
          </p>

          <p>
            This permission is only needed once for your browser.
          </p>
        </div>

        <ol className="oac-installation-page__steps">
          {installationSteps.map((step) => (
            <li
              className="oac-installation-page__step"
              key={step.id}
            >
              <span className="oac-installation-page__step-icon">
                {step.icon}
              </span>

              <span>{step.label}</span>
            </li>
          ))}
        </ol>

        <div className="oac-installation-page__button-artwork">
          <div
            aria-hidden="true"
            className="oac-android-mark"
          >
            <span className="oac-android-mark__antenna oac-android-mark__antenna--left" />
            <span className="oac-android-mark__antenna oac-android-mark__antenna--right" />
            <span className="oac-android-mark__eye oac-android-mark__eye--left" />
            <span className="oac-android-mark__eye oac-android-mark__eye--right" />
          </div>

          <ActionButton
            className="oac-installation-page__guide-button oac-installation-page__guide-button--route"
            to="/app-installation-guide/steps"
            variant="primary"
          >
            <span
              aria-hidden="true"
              className="oac-installation-page__document-icon"
            />

            <span>View Installation Guide</span>
          </ActionButton>

          <ActionButton
            className="oac-installation-page__guide-button oac-installation-page__guide-button--scroll"
            onClick={(event) => {
              if (
                typeof window === "undefined" ||
                !window.matchMedia("(max-width: 67.9375rem)").matches
              ) {
                return;
              }

              event.preventDefault();
              scrollToInstallationGuideSteps();
            }}
            to="#oac-installation-guide-steps"
            variant="primary"
          >
            <span
              aria-hidden="true"
              className="oac-installation-page__document-icon"
            />

            <span>View Installation Guide</span>
          </ActionButton>
        </div>
      </section>

      <section
        aria-label="Android app installation illustration"
        className="oac-installation-visual"
      >
        <div
          aria-label="Example Android installation screen"
          className="oac-phone"
          role="img"
        >
          <span
            aria-hidden="true"
            className="oac-phone__camera"
          />

          <div className="oac-phone__screen">
            <div className="oac-phone__status">
              <span>12:30</span>
              <span>● ●</span>
            </div>

            <div className="oac-phone__title-row">
              <span aria-hidden="true">‹</span>
              <strong>Install unknown apps</strong>
            </div>

            <div className="oac-phone__app-row">
              <span
                aria-hidden="true"
                className="oac-phone__chrome-mark"
              />

              <span>
                <strong>Chrome</strong>
                <small>Browser</small>
              </span>
            </div>

            <div className="oac-phone__permission-row">
              <strong>Allow from this source</strong>

              <span
                aria-hidden="true"
                className="oac-phone__switch"
              >
                <span />
              </span>
            </div>

            <p className="oac-phone__small-copy">
              Your phone and personal data are more vulnerable to
              attack by unknown apps.
            </p>

            <div className="oac-phone__installer">
              <span className="oac-phone__app-badge">C</span>

              <div>
                <strong>OpenAppCart.apk</strong>
                <p>Do you want to install this app?</p>
              </div>

              <div
                aria-hidden="true"
                className="oac-phone__installer-actions"
              >
                <span className="oac-phone__installer-action">Cancel</span>
                <span className="oac-phone__installer-action oac-phone__installer-action--primary">
                  Install
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>

      <section
        aria-labelledby="oac-installation-guide-steps-title"
        className="oac-installation-page__scroll-steps"
        id="oac-installation-guide-steps"
      >
        <h2 className="oac-visually-hidden" id="oac-installation-guide-steps-title">
          Detailed installation steps
        </h2>

        <AppInstallationGuideStepsSection showBackLink />
      </section>
    </>
  );
}
